import { supabase } from '@/integrations/supabase/client';

export interface BoardMember {
  id: string;
  company_id: string;
  user_id: string;
  full_name: string | null;
  role: 'CHAIR' | 'DIRECTOR' | 'OBSERVER';
  active: boolean;
  created_at: string;
}

export interface BoardResolution {
  id: string;
  company_id: string;
  title: string;
  body_md: string;
  status: 'DRAFT' | 'IN_CIRCULATION' | 'APPROVED' | 'REJECTED' | 'ARCHIVED';
  required_role: 'CHAIR' | 'DIRECTOR' | 'ANY';
  quorum_pct: number;
  created_by: string | null;
  created_at: string;
  approved_at: string | null;
}

/**
 * Send board consent email to a member
 */
export async function sendBoardConsentEmail(
  memberEmail: string,
  resolutionId: string,
  companyName: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data, error } = await supabase.functions.invoke('send-board-consent', {
      body: {
        memberEmail,
        resolutionId,
        companyName,
      },
    });

    if (error) {
      console.error('Error invoking send-board-consent function:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err: any) {
    console.error('Error sending board consent email:', err);
    return { success: false, error: err.message };
  }
}

/**
 * Get all board members for a company
 */
export async function getBoardMembers(companyId: string): Promise<BoardMember[]> {
  const { data, error } = await supabase
    .from('board_members')
    .select('*')
    .eq('company_id', companyId)
    .eq('active', true)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching board members:', error);
    throw error;
  }

  return (data || []) as BoardMember[];
}

/**
 * Add a new board member
 */
export async function addBoardMember(
  companyId: string,
  userId: string,
  fullName: string,
  role: 'CHAIR' | 'DIRECTOR' | 'OBSERVER'
): Promise<BoardMember> {
  const { data, error } = await supabase
    .from('board_members')
    .insert({
      company_id: companyId,
      user_id: userId,
      full_name: fullName,
      role,
      active: true,
    })
    .select()
    .single();

  if (error) {
    console.error('Error adding board member:', error);
    throw error;
  }

  return data as BoardMember;
}

/**
 * Circulate resolution to all eligible members
 */
export async function circulateResolution(resolutionId: string): Promise<number> {
  const { data, error } = await supabase.rpc('circulate_resolution', {
    p_resolution: resolutionId,
  });

  if (error) {
    console.error('Error circulating resolution:', error);
    throw error;
  }

  return data as number;
}

/**
 * Sign a resolution
 */
export async function signResolution(
  resolutionId: string,
  decision: 'APPROVE' | 'REJECT' | 'ABSTAIN'
): Promise<void> {
  const { error } = await supabase.rpc('sign_resolution', {
    p_resolution: resolutionId,
    p_decision: decision,
  });

  if (error) {
    console.error('Error signing resolution:', error);
    throw error;
  }
}

/**
 * Finalize a resolution
 */
export async function finalizeResolution(resolutionId: string): Promise<string> {
  const { data, error } = await supabase.rpc('finalize_resolution', {
    p_resolution: resolutionId,
  });

  if (error) {
    console.error('Error finalizing resolution:', error);
    throw error;
  }

  return data as string;
}
